/*
 * *****************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commerical BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   Thu, Jul 22nd 2021, 7:49:46 am
 *
 * *****************************************************
 */

import { useCallback, MouseEvent, useEffect } from 'react';

import { Button, Link, TextStyle } from '@shopify/polaris';
import { Helmet } from 'react-helmet';
import { RouteComponentProps, useHistory } from 'react-router-dom';

import { CREATE_WORKSPACE, SIGN_IN } from 'features/auth/routes/paths';
import PageErrorBanner from 'features/common/views/components/PageErrorBanner';
import { useGetWorkspacesQuery, useLazySignInToWorkspaceQuery } from 'states/services/auth/hooks';
import { selectEmail } from 'states/slices/auth/selectors';
import { Workspace } from 'states/slices/workspaces/types';
import { useAppSelector } from 'utils/hooks';

import AuthLayout from '../../layouts/AuthLayout';
import GetWorkspacesLoading from './GetWorkspacesLoading';
import RetryGetWorkspaces from './RetryGetWorkspaces';
import { ContentContainer, List, RetryGetWorkspaceContainer } from './styles';
import WorkspaceItem from './WorkspaceItem';
import WorkspaceSignInLoading from './WorkspaceSignInLoading';

/**
 * Select workspace page
 */
const SelectWorkspace = ({ location }: RouteComponentProps) => {
  const email = useAppSelector(selectEmail);
  const history = useHistory();
  const getWorkspaceStatus = useGetWorkspacesQuery(undefined, { refetchOnMountOrArgChange: true });
  /**
   * API trigger of RTK Query
   */
  const { refetch: reGetWorkspaces } = getWorkspaceStatus;
  /**
   * [apiTrigger, apiResult] of RTK Lazy Query
   */
  const [signInToWorkspace, signInToWorkspaceStatus] = useLazySignInToWorkspaceQuery({});
  const workspaceListData = (getWorkspaceStatus.data as Workspace[]) ?? [];
  const workspaceListDataLength = workspaceListData.length;

  const handleSignInToWorkspace = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      const { id: workspaceId } = e.currentTarget?.dataset;
      signInToWorkspace(workspaceId!);
    },
    [signInToWorkspace]
  );

  const handleCreateNewWorkspaceButton = useCallback(() => {
    history.push({
      pathname: CREATE_WORKSPACE,
      state: {
        referrer: location.pathname
      }
    });
  }, [history, location]);

  useEffect(() => {
    /**
     * Redirect user to workspace domain when successfully signing in to workspace
     */
    if (signInToWorkspaceStatus.isSuccess) {
      setTimeout(() => {
        window.location.replace(
          `${process.env.HTTPS ? 'https' : 'http'}://${process.env.REACT_APP_WORKSPACE_DOMAIN}`
        );
      }, 500);
    }
  }, [signInToWorkspaceStatus.isSuccess]);

  useEffect(() => {
    /**
     * Refetch workspace list data if error
     */
    if (getWorkspaceStatus.isError) {
      setTimeout(() => {
        reGetWorkspaces();
      }, 5000);
    }
  }, [getWorkspaceStatus.isError, reGetWorkspaces]);

  let pageContent = null;

  if (signInToWorkspaceStatus.isLoading || signInToWorkspaceStatus.isSuccess) {
    /**
     * If user is signing into a specific workspace or even success,
     * we will persist the loading until user is redirected to workspace domain
     */
    pageContent = <WorkspaceSignInLoading />;
  } else {
    /**
     * If user is not signing into a specific workspace
     */
    if (getWorkspaceStatus.isLoading) {
      /**
       * If page is getting workspace list data
       */
      pageContent = <GetWorkspacesLoading />;
    }
    if (getWorkspaceStatus.isError) {
      /**
       * If there's error when getting workspace list data
       */
      pageContent = (
        <RetryGetWorkspaceContainer>
          <PageErrorBanner
            data={{
              data: {
                message: 'Error(s) happened while getting data.'
              }
            }}
          />
          <RetryGetWorkspaces />
          <footer>
            <TextStyle variation="subdued">
              Logged in as <TextStyle variation="strong">{email}</TextStyle>. <br />
              Looking for another workspace?{' '}
              <Link removeUnderline url={SIGN_IN}>
                Try with another email
              </Link>
            </TextStyle>
          </footer>
        </RetryGetWorkspaceContainer>
      );
    }
    if (getWorkspaceStatus.isSuccess) {
      /**
       * If getting workspace list data successfully
       */
      pageContent = (
        <>
          <ContentContainer>
            {workspaceListDataLength !== 0 && (
              <>
                {signInToWorkspaceStatus.isError && (
                  <PageErrorBanner
                    data={{
                      data: {
                        message: 'Error(s) happened. Please try again later.'
                      }
                    }}
                  />
                )}
                <List>
                  {(getWorkspaceStatus.data as Workspace[]).map(({ name, color, id }) => (
                    <WorkspaceItem
                      key={id}
                      name={name}
                      color={color}
                      id={id}
                      onClick={handleSignInToWorkspace}
                    />
                  ))}
                </List>
              </>
            )}
            <Button primary onClick={handleCreateNewWorkspaceButton}>
              Create a new workspace
            </Button>
          </ContentContainer>
          <footer>
            <TextStyle variation="subdued">
              Logged in as <TextStyle variation="strong">{email}</TextStyle>. <br />
              Looking for another workspace?{' '}
              <Link removeUnderline url={SIGN_IN}>
                Try with another email
              </Link>
            </TextStyle>
          </footer>
        </>
      );
    }
  }

  return (
    <AuthLayout>
      <Helmet>
        <title>Select workspace | WiseIO</title>
        <meta name="description" content="Select workspace" />
        <meta property="og:url" content={window.location.href} />
        <meta name="robots" content="noindex" />
      </Helmet>
      <header>
        <h1>Your workspaces</h1>
        {!getWorkspaceStatus.isLoading && getWorkspaceStatus.isSuccess && (
          <TextStyle variation="subdued">
            {workspaceListDataLength === 0 ? (
              <>
                Let’s start the work by creating a new workspace <br />
                on WiseIO.
              </>
            ) : (
              <>
                Showing <TextStyle variation="strong">{workspaceListDataLength}</TextStyle>{' '}
                workspaces associated with your account.
              </>
            )}
          </TextStyle>
        )}
      </header>
      {pageContent}
    </AuthLayout>
  );
};

export default SelectWorkspace;
